import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useHistory, useLocation } from "react-router-dom";

import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Collapse,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@material-ui/core";
import { useTheme, makeStyles } from "@material-ui/core/styles";

import { FilledTextField, FilledSelect } from "../../components/FormElements";
import { PROMO_GROUP_TYPES, CURRENCIES } from "../../constants";
import { PromoContext, FleetContext } from "../../context";

import { promoGroupSchema } from "../../validation/schemas";
import { recParseObjectForForm } from "../../services/misc.service";

const useStyles = makeStyles((theme) => ({
  infoMessage: { opacity: 0.75 },
  marginRight: { marginRight: theme.spacing(1) },
  dateInput: { paddingRight: theme.spacing(1) },
  timeInput: { paddingLeft: theme.spacing(1) },
  dateTimeInput: {
    "& label": {
      width: "100%",
      "white-space": "nowrap",
      "text-overflow": "ellipsis",
      overflow: "hidden",
    },
  },
  fleetsFieldControl: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  fleetsField: {
    display: "flex",
    flexWrap: "wrap",
  },
  fleetsFieldItem: {
    background: theme.palette.background.dark,
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  tooltipIcon: { paddingLeft: theme.spacing(0.5) },
  tooltip: { fontSize: "0.75em" },
  fleetCell: { minWidth: "100px" },
  error: {
    padding: 0,
    background: "none",
  },
}));

const PromoGroupWrite = () => {
  const MODES = ["new", "edit"];
  const initialForm = {
    name: "",
    active: true,
    currency: "USD",
    description: "",
    promo_code_type: "discount",
    amount: 0,
    percent_off: 0,
    prefix: "",
    num_codes: 1,
    max_redemptions: 1,
    expired: "",
    fleets: [],
  };
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { promoGroupId } = useParams();
  const {
    getActivePromoGroup,
    activePromoGroup,
    getPromoCodesByGroup,
    promoCodesByGroup,
    promoCodesCount,
    createCouponGroup,
    updateCouponGroup,
  } = useContext(PromoContext);
  const { fleets } = useContext(FleetContext);

  const [open, setOpen] = useState(true);
  const [mode, setMode] = useState(MODES[0]);
  const [dirtyFields, setDirtyFields] = useState([]);
  const [invalids, setInvalids] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [promoTypeField, setPromoTypeField] = useState(PROMO_GROUP_TYPES[0]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const data = promoGroupSchema.cast(form, { stripUnknown: true });
    data.expired = data.expired.toISOString().replace(/T.*/, "T23:59:59.000Z");
    data.expired_by = data.expired;
    data.prefix = data.prefix.toUpperCase();
    data.name = data.name.toUpperCase();
    if (mode === "edit") {
      await updateCouponGroup(promoGroupId, { active: data.active });
    } else {
      await createCouponGroup(data);
    }
    setLoading(false);
    handleClose();
  };

  const findValidationError = (field) => {
    if (!dirtyFields.includes(field)) return null;
    return invalids.find((err) => err.includes(field));
  };

  useEffect(() => {
    setMode(MODES.find((givenPath) => location.pathname.includes(`/${givenPath}`)));
  }, [mode, location.pathname]);

  useEffect(() => {
    if (mode === "edit") getActivePromoGroup(promoGroupId);
  }, [mode, location.pathname]);

  useEffect(() => {
    const updatePromoCodesData = async () => {
      setLoading(true);
      await getPromoCodesByGroup(promoGroupId, promoTypeField);
      setLoading(false);
    };
    if (promoGroupId && mode === "edit") updatePromoCodesData();
  }, [promoTypeField]);

  useEffect(() => {
    setPromoTypeField(activePromoGroup?.promo_code_type);
  }, [activePromoGroup, location.pathname]);

  useEffect(() => {
    if (activePromoGroup && promoGroupId) {
      const parsed = recParseObjectForForm(activePromoGroup);
      const truncateExpired = parsed.expired
        ? parsed.expired.slice(0, 10)
        : promoCodesByGroup[0]?.expired.slice(0, 10);
      setForm({
        ...initialForm,
        ...parsed,
        expired: truncateExpired,
        num_codes: promoCodesCount,
        percent_off: promoCodesByGroup[0]?.percent_off || 0,
        amount: promoCodesByGroup[0]?.amount || 0,
        active: promoCodesByGroup[0]?.active,
        fleets: promoCodesByGroup[0]?.fleets,
      });
    } else setForm(initialForm);
  }, [activePromoGroup, promoCodesByGroup, promoCodesCount]);

  useEffect(() => {
    const validate = async () => {
      try {
        await promoGroupSchema.validate(form, {
          abortEarly: false,
          recursive: true,
        });
        setInvalids([]);
      } catch (err) {
        setInvalids(err.errors);
      }
    };
    validate();
  }, [form, promoTypeField]);

  const standardTextField = (field, rest) => (
    <FilledTextField
      key={form?.currency}
      value={form[field]}
      label={t(`Promos.PromoGroups.Modal.${field}`)}
      onChange={(e) =>
        setForm({
          ...form,
          [field]: e.target.value,
        })
      }
      onBlur={() => setDirtyFields([...dirtyFields, field])}
      underlineColor={theme.palette.text.lightgrey}
      helperText={findValidationError(field)}
      error={!!findValidationError(field)}
      {...rest}
    />
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t(`Promos.PromoGroups.Modal.${mode === "new" ? "CreateGroup" : "EditGroup"}`)}
      </DialogTitle>
      <DialogContent>
        <Grid>{standardTextField("name", { disabled: mode === "edit" })}</Grid>
        <Grid>
          {standardTextField("description", { multiline: true, disabled: mode === "edit" })}
        </Grid>
        <Grid>
          <FilledSelect
            value={promoTypeField}
            label={t("Promos.PromoGroups.Modal.type")}
            onChange={(e) => {
              setPromoTypeField(e.target.value);
              setForm({
                ...form,
                promo_code_type: e.target.value,
              });
            }}
            FormControlProps={{ fullWidth: true, margin: "normal", disabled: mode === "edit" }}
          >
            {PROMO_GROUP_TYPES.map((type) => (
              <MenuItem value={type} key={type}>
                {t(`Promos.Nav.${type === "unlock" ? "unlock" : type}`)}
              </MenuItem>
            ))}
          </FilledSelect>
        </Grid>

        <Collapse in={!!promoTypeField && promoTypeField !== "unlock"}>
          <Grid container justify="space-between">
            {promoTypeField === "bonus" ? (
              <>
                <Grid item style={{ paddingRight: theme.spacing(2) }} xs={12} md={5}>
                  <FilledSelect
                    value={form?.currency}
                    label={t("Promos.PromoGroups.Modal.currency")}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    helperText={findValidationError("currency")}
                    FormControlProps={{
                      fullWidth: true,
                      margin: "normal",
                      disabled: mode === "edit",
                    }}
                    error={!!findValidationError("currency")}
                  >
                    {CURRENCIES.map((cur) => (
                      <MenuItem value={cur} key={cur}>
                        {cur}
                      </MenuItem>
                    ))}
                  </FilledSelect>
                </Grid>
                <Grid item xs={12} md={7}>
                  {standardTextField("amount", { mask: form.currency, disabled: mode === "edit" })}
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                {standardTextField("percent_off", {
                  mask: "PERCENT_WHOLE",
                  disabled: mode === "edit",
                })}
              </Grid>
            )}
          </Grid>
        </Collapse>
        <Grid>{standardTextField("prefix", { disabled: mode === "edit" })}</Grid>
        <Grid>{standardTextField("num_codes", { disabled: mode === "edit" })}</Grid>
        <Grid>
          {standardTextField("expired", {
            type: "date",
            disabled: mode === "edit",
            InputLabelProps: { shrink: true },
          })}
        </Grid>
        <Grid item xs={12}>
          <FilledSelect
            label={t("Promos.EditModal.fleets")}
            value={form?.fleets || initialForm.fleets}
            onChange={(e) => {
              setForm({ ...form, fleets: e.target.value });
            }}
            FormControlProps={{
              disabled: mode === "edit",
              className: classes.fleetsFieldControl,
            }}
            SelectProps={{
              multiple: true,
              renderValue: (selected) => (
                <div className={classes.fleetsField}>
                  {selected.map((uuid) => (
                    <Chip
                      key={uuid}
                      label={fleets.map((fleet) => {
                        if (fleet.uuid4 === uuid) {
                          return fleet.state ? `${fleet.name}, ${fleet.state}` : fleet.name;
                        }
                        return "";
                      })}
                      className={classes.fleetsFieldItem}
                    />
                  ))}
                </div>
              ),
            }}
          >
            {fleets.map((fleet) => (
              <MenuItem key={fleet.uuid4} value={fleet.uuid4}>
                {fleet.state ? `${fleet.name}, ${fleet.state}` : fleet.name}
              </MenuItem>
            ))}
          </FilledSelect>
        </Grid>
        {loading && <LinearProgress color="secondary" />}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={form?.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                value="active"
              />
            }
            label={t("Promos.PromoGroups.Modal.active")}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t("Promos.PromoGroups.Modal.Cancel")}</Button>
        <Button
          onClick={
            mode === "new"
              ? handleSubmit
              : () => {
                  setShowConfirmModal(true);
                }
          }
          disabled={loading || invalids?.length > 0}
        >
          {t("Promos.PromoGroups.Modal.Save")}
        </Button>
        {showConfirmModal && (
          <Dialog open={showConfirmModal}>
            <DialogTitle>{t("Promos.PromoGroups.Modal.ConfirmationTitle")}</DialogTitle>
            <DialogContent>{t("Promos.PromoGroups.Modal.ConfirmationText")}</DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowConfirmModal(false);
                }}
              >
                {t("Promos.PromoGroups.Modal.Cancel")}
              </Button>
              <Button
                onClick={() => {
                  handleSubmit();
                  setShowConfirmModal(false);
                }}
              >
                {t("Promos.PromoGroups.Modal.Save")}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PromoGroupWrite;
